import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

// Try to import recharts, fallback to simple charts if not available
let RechartsAvailable = false;
let LineChart, BarChart, PieChart, Pie, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell;
try {
  const recharts = require('recharts');
  LineChart = recharts.LineChart;
  BarChart = recharts.BarChart;
  PieChart = recharts.PieChart;
  Pie = recharts.Pie;
  Line = recharts.Line;
  Bar = recharts.Bar;
  XAxis = recharts.XAxis;
  YAxis = recharts.YAxis;
  CartesianGrid = recharts.CartesianGrid;
  Tooltip = recharts.Tooltip;
  Legend = recharts.Legend;
  ResponsiveContainer = recharts.ResponsiveContainer;
  Cell = recharts.Cell;
  RechartsAvailable = true;
} catch (e) {
  console.warn('Recharts not available, using simple charts');
}

const EquipmentDashboardPage = () => {
  const navigate = useNavigate();
  const { equipment, loading: equipmentLoading } = useEquipment();
  const { currentTeam } = useTeam();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter equipment
  const smallItems = equipment.filter(eq => eq.type === EQUIPMENT_TYPES.SMALL_ITEM);
  const largeItems = equipment.filter(eq => eq.type === EQUIPMENT_TYPES.LARGE_ITEM);

  // Calculate statistics
  const stats = {
    totalEquipment: equipment.length,
    totalSmallItems: smallItems.length,
    totalLargeItems: largeItems.length,
    totalStock: smallItems.reduce((sum, eq) => sum + (eq.currentStock || 0), 0),
    lowStockItems: smallItems.filter(eq => {
      const stock = eq.currentStock || 0;
      const minStock = eq.minStock || 0;
      return stock < minStock && minStock > 0;
    }),
    outOfStockItems: smallItems.filter(eq => (eq.currentStock || 0) <= 0),
    availableLargeItems: largeItems.filter(eq => eq.status === EQUIPMENT_STATUS.AVAILABLE).length,
    borrowedLargeItems: largeItems.filter(eq => eq.status === EQUIPMENT_STATUS.BORROWED).length,
    repairLargeItems: largeItems.filter(eq => eq.status === EQUIPMENT_STATUS.REPAIR).length
  };

  const [stockHistory, setStockHistory] = useState([]);
  const [inspectionHistory, setInspectionHistory] = useState([]);

  // Fetch inspections for selected date
  useEffect(() => {
    const fetchInspections = async () => {
      if (!currentTeam) return;

      try {
        setLoading(true);
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        const inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id),
          where('date', '>=', Timestamp.fromDate(startOfDay)),
          where('date', '<=', Timestamp.fromDate(endOfDay))
        );
        const snapshot = await getDocs(inspectionsQuery);
        const inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInspections(inspectionsData);
      } catch (err) {
        console.error('Error fetching inspections:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInspections();
  }, [currentTeam, selectedDate]);

  // Fetch stock history for charts (last 7 days)
  useEffect(() => {
    const fetchStockHistory = async () => {
      if (!currentTeam) return;

      try {
        const endDate = new Date(selectedDate);
        const startDate = new Date(selectedDate);
        startDate.setDate(startDate.getDate() - 7);

        const transactionsQuery = query(
          collection(db, 'equipmentStockTransactions'),
          where('teamId', '==', currentTeam.id),
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          where('createdAt', '<=', Timestamp.fromDate(endDate))
        );
        const snapshot = await getDocs(transactionsQuery);
        const transactionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Group by date
        const groupedByDate = {};
        transactionsData.forEach(tx => {
          const date = tx.createdAt?.toDate ? tx.createdAt.toDate() : new Date();
          const dateKey = date.toISOString().split('T')[0];
          if (!groupedByDate[dateKey]) {
            groupedByDate[dateKey] = { date: dateKey, stockIn: 0, stockOut: 0 };
          }
          if (tx.type === 'STOCK_IN' && tx.status === 'APPROVED') {
            groupedByDate[dateKey].stockIn += tx.quantity || 0;
          } else if (tx.type === 'STOCK_OUT' && tx.status === 'APPROVED') {
            groupedByDate[dateKey].stockOut += tx.quantity || 0;
          }
        });

        setStockHistory(Object.values(groupedByDate).sort((a, b) => a.date.localeCompare(b.date)));
      } catch (err) {
        console.error('Error fetching stock history:', err);
      }
    };

    fetchStockHistory();
  }, [currentTeam, selectedDate]);

  // Fetch inspection history for charts (last 7 days)
  useEffect(() => {
    const fetchInspectionHistory = async () => {
      if (!currentTeam) return;

      try {
        const endDate = new Date(selectedDate);
        const startDate = new Date(selectedDate);
        startDate.setDate(startDate.getDate() - 7);

        const inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id),
          where('date', '>=', Timestamp.fromDate(startDate)),
          where('date', '<=', Timestamp.fromDate(endDate))
        );
        const snapshot = await getDocs(inspectionsQuery);
        const inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Group by date
        const groupedByDate = {};
        inspectionsData.forEach(inspection => {
          const date = inspection.date?.toDate ? inspection.date.toDate() : new Date();
          const dateKey = date.toISOString().split('T')[0];
          if (!groupedByDate[dateKey]) {
            groupedByDate[dateKey] = { date: dateKey, inspections: 0, damaged: 0, lost: 0 };
          }
          groupedByDate[dateKey].inspections += 1;
          groupedByDate[dateKey].damaged += inspection.totalDamaged || 0;
          groupedByDate[dateKey].lost += inspection.totalLost || 0;
        });

        setInspectionHistory(Object.values(groupedByDate).sort((a, b) => a.date.localeCompare(b.date)));
      } catch (err) {
        console.error('Error fetching inspection history:', err);
      }
    };

    fetchInspectionHistory();
  }, [currentTeam, selectedDate]);

  // Chart data
  const stockChartData = stockHistory.map(item => ({
    date: new Date(item.date).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' }),
    'นำเข้า': item.stockIn,
    'นำออก': item.stockOut
  }));

  const inspectionChartData = inspectionHistory.map(item => ({
    date: new Date(item.date).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' }),
    'การตรวจสอบ': item.inspections,
    'เสียหาย': item.damaged,
    'สูญหาย': item.lost
  }));

  const statusChartData = [
    { name: 'พร้อมใช้', value: stats.availableLargeItems, color: '#10b981' },
    { name: 'ถูกยืม', value: stats.borrowedLargeItems, color: '#f59e0b' },
    { name: 'อยู่ระหว่างส่งซ่อม', value: stats.repairLargeItems, color: '#ef4444' }
  ];

  if (equipmentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Dashboard อุปกรณ์</h1>
            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">อุปกรณ์ทั้งหมด</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalEquipment}</p>
              </div>
              <div className="text-3xl">🩺</div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              ชิ้นเล็ก: {stats.totalSmallItems} | ชิ้นใหญ่: {stats.totalLargeItems}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Stock ทั้งหมด</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalStock}</p>
              </div>
              <div className="text-3xl">📦</div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              อุปกรณ์ชิ้นเล็ก
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Stock ต่ำ</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.lowStockItems.length}</p>
              </div>
              <div className="text-3xl">⚠️</div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              ต้องเติม Stock
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">หมด Stock</p>
                <p className="text-2xl font-bold text-red-600">{stats.outOfStockItems.length}</p>
              </div>
              <div className="text-3xl">❌</div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              ต้องสั่งซื้อด่วน
            </div>
          </div>
        </div>

        {/* Alerts */}
        {(stats.lowStockItems.length > 0 || stats.outOfStockItems.length > 0) && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">⚠️ แจ้งเตือน</h2>
            
            {stats.outOfStockItems.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-red-700 mb-2">❌ หมด Stock ({stats.outOfStockItems.length} รายการ)</h3>
                <div className="space-y-2">
                  {stats.outOfStockItems.map(eq => (
                    <div key={eq.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="font-medium text-gray-800">{eq.name} ({eq.code})</p>
                      <p className="text-sm text-gray-600">Stock: {eq.currentStock || 0} {eq.unit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.lowStockItems.length > 0 && (
              <div>
                <h3 className="font-semibold text-yellow-700 mb-2">⚠️ Stock ต่ำ ({stats.lowStockItems.length} รายการ)</h3>
                <div className="space-y-2">
                  {stats.lowStockItems.slice(0, 5).map(eq => (
                    <div key={eq.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="font-medium text-gray-800">{eq.name} ({eq.code})</p>
                      <p className="text-sm text-gray-600">
                        Stock: {eq.currentStock || 0} / ต่ำสุด: {eq.minStock || 0} {eq.unit}
                      </p>
                    </div>
                  ))}
                  {stats.lowStockItems.length > 5 && (
                    <p className="text-sm text-gray-600 mt-2">
                      และอีก {stats.lowStockItems.length - 5} รายการ...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Stock Movement Chart */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📊 การเคลื่อนไหว Stock (7 วันล่าสุด)</h2>
            {RechartsAvailable && stockChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stockChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="นำเข้า" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="นำออก" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : stockChartData.length > 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="mb-2">กราฟ Stock Movement</p>
                  <p className="text-sm">ติดตั้ง recharts เพื่อดูกราฟ</p>
                  <p className="text-xs mt-2">npm install recharts --legacy-peer-deps</p>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <p>ไม่มีข้อมูลในช่วง 7 วันที่ผ่านมา</p>
              </div>
            )}
          </div>

          {/* Inspection Chart */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">🔍 การตรวจสอบ (7 วันล่าสุด)</h2>
            {RechartsAvailable && inspectionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={inspectionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="การตรวจสอบ" fill="#3b82f6" />
                  <Bar dataKey="เสียหาย" fill="#f59e0b" />
                  <Bar dataKey="สูญหาย" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            ) : inspectionChartData.length > 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="mb-2">กราฟการตรวจสอบ</p>
                  <p className="text-sm">ติดตั้ง recharts เพื่อดูกราฟ</p>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <p>ไม่มีข้อมูลในช่วง 7 วันที่ผ่านมา</p>
              </div>
            )}
          </div>
        </div>

        {/* Large Items Status with Pie Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">🩺 สถานะอุปกรณ์ชิ้นใหญ่</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">พร้อมใช้</p>
                <p className="text-2xl font-bold text-green-700">{stats.availableLargeItems}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">ถูกยืม</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.borrowedLargeItems}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">อยู่ระหว่างส่งซ่อม</p>
                <p className="text-2xl font-bold text-red-700">{stats.repairLargeItems}</p>
              </div>
            </div>
            {RechartsAvailable && largeItems.length > 0 && (
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Today's Inspections */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">
            📋 การตรวจสอบวันนี้ ({selectedDate.toLocaleDateString('th-TH')})
          </h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>ยังไม่มีการตรวจสอบวันนี้</p>
              <Link
                to="/nurse/equipment/inspection"
                className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                เริ่มตรวจสอบ
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {inspections.map(inspection => {
                const date = inspection.date?.toDate ? inspection.date.toDate() : new Date();
                return (
                  <div key={inspection.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {inspection.round === 'MORNING_BEFORE' ? 'เช้า - ก่อนเข้าเวร' :
                           inspection.round === 'MORNING_AFTER' ? 'เช้า - หลังเข้าเวร' :
                           inspection.round === 'AFTERNOON_BEFORE' ? 'บ่าย - ก่อนเข้าเวร' :
                           inspection.round === 'AFTERNOON_AFTER' ? 'บ่าย - หลังเข้าเวร' :
                           inspection.round === 'NIGHT_BEFORE' ? 'ดึก - ก่อนเข้าเวร' :
                           'ดึก - หลังเข้าเวร'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          โดย: {inspection.inspectedByName} | {date.toLocaleTimeString('th-TH')}
                        </p>
                        <p className="text-sm text-gray-600">
                          ตรวจสอบ: {inspection.totalItems} รายการ
                        </p>
                        {(inspection.totalDamaged > 0 || inspection.totalLost > 0) && (
                          <p className="text-sm text-yellow-600 mt-1">
                            ⚠️ เสียหาย: {inspection.totalDamaged}, สูญหาย: {inspection.totalLost}
                          </p>
                        )}
                      </div>
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                        ✅ เสร็จ
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/nurse/equipment/inspection"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">🔍</div>
            <h3 className="font-semibold text-gray-800">ตรวจสอบอุปกรณ์</h3>
            <p className="text-sm text-gray-600 mt-2">เริ่มการตรวจสอบ</p>
          </Link>
          <Link
            to="/nurse/equipment/stock"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">📥</div>
            <h3 className="font-semibold text-gray-800">นำเข้า Stock</h3>
            <p className="text-sm text-gray-600 mt-2">บันทึก Stock In</p>
          </Link>
          <Link
            to="/nurse/equipment"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">📋</div>
            <h3 className="font-semibold text-gray-800">รายการอุปกรณ์</h3>
            <p className="text-sm text-gray-600 mt-2">ดูรายการทั้งหมด</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDashboardPage;

